import React, { FC, useEffect, useState } from 'react'
import {
  Checkbox,
  FormControl,
  Grid,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
  Theme,
  Typography,
} from '@mui/material'
import { Control, Controller, FieldPath, FieldValues, PathValue } from 'react-hook-form'
import { makeStyles } from '@mui/styles'
import { alpha } from '@mui/material/styles'

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    width: 250,
    display: 'flex',
  },
  label: {
    fontSize: '18px',
    marginBottom: '-3px',
    color: '#FFFFFF',
    opacity: '60%',
  },
  formControl: {
    width: 'inherit',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
  },
  select: {
    width: 'inherit',
    maxHeight: 40,
    '& .MuiSelect-icon': {
      color: alpha(theme.palette.primary.light, 0.6),
    },
  },
  noOptions: {
    color: theme.palette.primary.light,
    padding: theme.spacing(2),
  },
  box: {
    color: theme.palette.primary.light,
  },
  chip: {
    color: theme.palette.primary.light,
  },
  checkbox: {
    color: alpha(theme.palette.primary.light, 0.6),
    marginRight: theme.spacing(1),
  },
}))

interface MultiSelectProps<T extends FieldValues, TName extends FieldPath<T>> {
  inputName: TName
  labelName?: string
  placeholder: string
  control: Control<T>
  loading: boolean
  options: { value: number; label: string }[]
  firstLoad: () => void
  // eslint-disable-next-line
  setValue: (name: TName, value: any) => void
  defaultValue?: number[]
}

export const MultiAssetsSelect = <T extends FieldValues, TName extends FieldPath<T>>({
  inputName,
  labelName,
  placeholder,
  control,
  loading,
  options,
  firstLoad,
  setValue,
  defaultValue = [],
}: MultiSelectProps<T, TName>): ReturnType<FC<MultiSelectProps<T, TName>>> => {
  const classes = useStyles()
  const [selectedId, setSelectedId] = useState<number[]>(defaultValue)

  useEffect(() => {
    firstLoad()
  }, [firstLoad])

  const handleChange = (event: SelectChangeEvent<typeof selectedId>) => {
    const {
      target: { value },
    } = event
    const newValue = (() => {
      if (typeof value === 'string') {
        return [...selectedId, ...value.split(',').map(Number)]
      }
      if (Array.isArray(value)) {
        return value
      }
      return [...selectedId, value]
    })()

    setSelectedId(newValue)
    // eslint-disable-next-line
    setValue('additionalAssets' as TName, newValue as any)
  }

  useEffect(() => {
    if (defaultValue && JSON.stringify(defaultValue) !== JSON.stringify(selectedId)) {
      setSelectedId(defaultValue)
    }
    // eslint-disable-next-line
  }, [defaultValue])

  return (
    <Grid container className={classes.container}>
      <Grid item>
        {labelName && (
          <InputLabel shrink htmlFor={inputName} className={classes.label}>
            {labelName}
          </InputLabel>
        )}
      </Grid>
      <FormControl className={classes.formControl}>
        <Controller
          name={inputName}
          control={control}
          defaultValue={[] as PathValue<T, TName>}
          render={({ field }) => (
            <Select
              {...field}
              className={classes.select}
              multiple
              placeholder={placeholder}
              onChange={handleChange}
              value={selectedId}
              renderValue={(selected) => {
                const selectedLabels = options
                  .filter((option) => (selected as number[]).includes(option.value))
                  .map((option) => option.label)
                return selectedLabels.join(', ')
              }}
            >
              {options.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <Checkbox
                    checked={selectedId.includes(option.value)}
                    className={classes.checkbox}
                    color="secondary"
                  />
                  <ListItemText primary={option.label} />
                </MenuItem>
              ))}
              {loading && (
                <MenuItem disabled>
                  <Typography variant="body2">Loading...</Typography>
                </MenuItem>
              )}
            </Select>
          )}
        />
      </FormControl>
    </Grid>
  )
}
